import React from 'react';
import { ModalBody } from 'ui/modal';
import { WarningBanner } from 'ui/banner';
import { DefineFieldTypes } from 'modules/catalogFieldTypes/components/DefineFieldTypes';
import { MODE } from 'modules/catalogFieldTypes/constants';
import { UPDATE_CATALOG_ITEM_METHOD } from 'modules/catalogTable/constants';
import { getSampleDataFromCsv, getSampleDataFromJson } from 'modules/catalogFieldTypes/helpers';
import CatalogCsvUploader from './CatalogCsvUploader';
import { CatalogItemValueJsonUploader } from './CatalogItemValueJsonUploader';
import { StyledWarningBanner } from './CatalogForm.styled';

// type Props = {
//   isCatalogCollectionsFlagOn: boolean,
//   steps: Array<*>,
//   step: number,
//   skipDefine: boolean,
//   tableKey: ?string,
//   tableValue: ?string,
//   updateMethod: ?string,
//   handleKeyChange: (*) => void,
//   handleValueChange: (*) => void,
//   handleJsonOnUpdate: (*) => void,
//   csvRowData: Array<Object>,
//   csvColumnData: Array<Object>,
//   onCsvPreview: (*) => void,
//   keyWarning: boolean,
//   headerValueWarning: boolean,
//   csvFileName: ?string,
//   csvFileError: ?string,
//   newFields: Array<*>,
//   dropdownOnChangeHandler: (*, *) => void,
//   fields: Object,
//   suggestedTypes: Object,
//   nestedFieldsWarning: boolean,
// };

export default class CatalogFormModalContent extends React.Component {
  constructor(props) {
    super(props);
    this.modalRef = React.createRef();
    this.state = {
      isStepChanged: true,
    };
  }
  componentDidUpdate(prevProps) {
    const { step } = this.props;
    if (prevProps.step !== step) {
      this.setState({ isStepChanged: true });
    }
    if (prevProps.step === step && this.state.isStepChanged) {
      this.setState({ isStepChanged: false });
    }
  }
  renderNewFieldRows = () => {
    const { updateMethod, newFields, tableValue, csvRowData } = this.props;
    if (updateMethod === UPDATE_CATALOG_ITEM_METHOD.JSON) {
      return getSampleDataFromJson({ newFields, tableValue });
    }
    return getSampleDataFromCsv({ newFields, rowData: csvRowData });
  };

  renderSkipWarning = (
    <WarningBanner>
      Are you sure?
      <div>
        <p>
          New fields must be defined if you want these fields to be <strong>queryable</strong>. If
          you choose to define fields <em>after</em> you upload this data to the catalog, this data
          will remain un-queryable until you re-upload them.
        </p>
        <p>
          <strong>
            For ease of use, Iterable recommends defining field types during updates to a catalog
          </strong>
        </p>
      </div>
    </WarningBanner>
  );

  renderStep1 = () => {
    const {
      tableKey,
      tableValue,
      handleKeyChange,
      handleValueChange,
      handleJsonOnUpdate,
      isCatalogCollectionsFlagOn,
      newFields,
      csvRowData,
      csvColumnData,
      onCsvPreview,
      keyWarning,
      headerValueWarning,
      csvFileName,
      csvFileError,
      nestedFieldsWarning,
      updateMethod,
    } = this.props;
    if (this.modalRef.current && this.state.isStepChanged) {
      this.modalRef.current.scrollIntoView?.();
    }
    return (
      <>
        {updateMethod === UPDATE_CATALOG_ITEM_METHOD.JSON ? (
          <div data-test="json-uploader">
            <CatalogItemValueJsonUploader
              tableKey={tableKey}
              tableValue={tableValue}
              handleKeyChange={handleKeyChange}
              handleValueChange={handleValueChange}
              handleJsonOnEdit={handleJsonOnUpdate}
              handleJsonOnAdd={handleJsonOnUpdate}
              handleJsonOnDelete={handleJsonOnUpdate}
              isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
              newFields={newFields}
            />
          </div>
        ) : (
          <div data-test="csv-uploader">
            <CatalogCsvUploader
              isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
              rowData={csvRowData}
              columnData={csvColumnData}
              onCsvPreview={onCsvPreview}
              keyWarning={keyWarning}
              headerValueWarning={headerValueWarning}
              csvFileName={csvFileName}
              csvFileError={csvFileError}
              newFields={newFields}
            />
          </div>
        )}
        {nestedFieldsWarning && isCatalogCollectionsFlagOn && (
          <StyledWarningBanner>
            <p>We noticed you have nested objects</p>
            <p>
              We don&apos;t recommend using nested objects as they are not supported in Catalog
              Collections. If you know you will never use this field in a query and wish to leave it
              un-typed, you may continue using this nested object.
            </p>
            <p>
              If, however, you decide to declare the type of the parent object as type
              &quot;object&quot;, you will not be able to have children that are also of type
              &quot;object&quot;
            </p>
          </StyledWarningBanner>
        )}
      </>
    );
  };

  renderStep2 = () => {
    const { dropdownOnChangeHandler, fields, newFields, skipDefine } = this.props;
    if (this.modalRef.current && this.state.isStepChanged) {
      this.modalRef.current.scrollIntoView?.();
    }
    return (
      <>
        {skipDefine ? (
          this.renderSkipWarning
        ) : (
          <div data-test="define-field-types">
            <DefineFieldTypes
              dropdownOnChangeHandler={dropdownOnChangeHandler}
              viewMode={MODE.define}
              fields={fields}
              undefinedFields={newFields}
              rows={this.renderNewFieldRows()}
            />
          </div>
        )}
      </>
    );
  };

  renderStep3 = () => {
    const { dropdownOnChangeHandler, fields, newFields, skipDefine, updateMethod } = this.props;
    if (this.modalRef.current && this.state.isStepChanged) {
      this.modalRef.current.scrollIntoView?.();
    }
    return (
      <>
        {skipDefine ? (
          this.renderSkipWarning
        ) : (
          <div data-test="define-field-types">
            <DefineFieldTypes
              dropdownOnChangeHandler={dropdownOnChangeHandler}
              viewMode={MODE.confirm}
              fields={fields}
              undefinedFields={newFields}
              rows={this.renderNewFieldRows()}
              showSingleSample={updateMethod === UPDATE_CATALOG_ITEM_METHOD.JSON}
            />
          </div>
        )}
      </>
    );
  };

  render() {
    const { isCatalogCollectionsFlagOn, step } = this.props;
    return (
      <ModalBody>
        <div ref={this.modalRef}>
          {(!isCatalogCollectionsFlagOn || (isCatalogCollectionsFlagOn && step === 1)) &&
            this.renderStep1()}
          {isCatalogCollectionsFlagOn && step === 2 && this.renderStep2()}
          {isCatalogCollectionsFlagOn && step === 3 && this.renderStep3()}
        </div>
      </ModalBody>
    );
  }
}
